import React, { useState, useEffect } from "react";
import { db, storage } from "../../utils/Firebase-Config";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { UserAuth } from "../../utils/AuthContext";
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UpdateIcon from '@mui/icons-material/Update';

function CreatePortfolio() {
  const { user } = UserAuth();

  // Unified States
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [rootDomain, setRootDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [isAvailable, setIsAvailable] = useState(null);
  const [checking, setChecking] = useState(false);
  const [hasPortfolio, setHasPortfolio] = useState(null);
  // eslint-disable-next-line
  const [checkingPortfolio, setCheckingPortfolio] = useState(true);

  useEffect(() => {
    if (!username || username.trim() === "") {
      setIsAvailable(null);
      setChecking(false);
      return;
    }

    const checkExistence = async () => {
      setChecking(true);
      try {
        const cleanId = username.toLowerCase().replace(/[^a-z0-9]/g, "");
        const docRef = doc(db, "portfolios", cleanId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setIsAvailable(false);
        } else {
          setIsAvailable(true);
        }
      } catch (error) {
        console.error("Error checking subdomain existence:", error);
        setIsAvailable(null);
      } finally {
        setChecking(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      checkExistence();
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [username]);
  
  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      setRootDomain("localhost:3000"); 
    } else {
      setRootDomain(hostname);
    }
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!user)
      return setStatusMessage("You must be logged in to create a portfolio.");

    const cleanUsername = username.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!cleanUsername)
      return setStatusMessage("Please enter a valid username.");

    if (isAvailable === false) {
      return setStatusMessage(
        `❌ ${rootDomain}/${cleanUsername} is already taken!`
      );
    }

    setLoading(true);
    setStatusMessage("Deploying your portfolio...");

    try {
      // ✅ FIX: Include the cleanUsername as the second argument (2 segments: portfolios/username)
      const portfolioDocRef = doc(db, "portfolios", cleanUsername); 
      const docSnap = await getDoc(portfolioDocRef);

      if (docSnap.exists()) {
        setLoading(false);
        setIsAvailable(false);
        return setStatusMessage(
          `❌ ${rootDomain}/${cleanUsername} is already taken!`
        );
      }

      setStatusMessage("Saving your portfolio...");
      
      // ✅ FIX: Use setDoc with your portfolioDocRef to enforce the custom username ID
      await setDoc(portfolioDocRef, {
        ownerUid: user.uid,
        username: cleanUsername,
        themeColor: "#4f46e5",
        createdAt: new Date().toISOString(),
      });

      setStatusMessage(`🎉 Success! Redirecting...`);

      setTimeout(() => {
        const protocol = window.location.protocol;
        window.location.href = `${protocol}//${rootDomain}/${cleanUsername}`;
      }, 1500);
    } catch (error) {
      console.error("Error creating portfolio:", error);
      setStatusMessage("An error occurred. Please try again.");
      setLoading(false);
    }
  };


  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!user)
      return setStatusMessage("You must be logged in to create a portfolio.");

    try {
      const portfolioDocRef = doc(db, "portfolios", username);
      let imageUrl = "";

      if (imageFile) {
        setStatusMessage("Uploading profile image...");
        const storageRef = ref(storage, `portfolios/${username}/profile.jpg`);
        const uploadResult = await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(uploadResult.ref);
      }

      setStatusMessage("Saving your portfolio...");
      await setDoc(
        portfolioDocRef,
        {
          displayName: displayName || username,
          bio: bio || "",
          profileImage: imageUrl,
          UpdatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setStatusMessage(`🎉 Updated!`);
    } catch (error) {
      console.error("Error creating portfolio:", error);
      setStatusMessage("An error occurred. Please try again.");
      setLoading(false);
    }
  };

useEffect(() => {
  if (!user) {
    setHasPortfolio(false);
    setCheckingPortfolio(false);
    return;
  }

  const checkPortfolioPeriod = async () => {
    try {
      const portfoliosRef = collection(db, "portfolios");
      const q = query(
        portfoliosRef,
        where("ownerUid", "==", user.uid),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setHasPortfolio(true);
        
        // PRE-POPULATE STATES FOR THE UPDATE FORM
        const portfolioDoc = querySnapshot.docs[0];
        const pData = portfolioDoc.data();
        setUsername(pData.username || "");
        setDisplayName(pData.displayName || "");
        setBio(pData.bio || "");
      } else {
        setHasPortfolio(false);
      }
    } catch (error) {
      console.error("Error checking portfolio existence:", error);
      setHasPortfolio(false);
    } finally {
      setCheckingPortfolio(false);
    }
  };

  checkPortfolioPeriod();
}, [user]);


  return (
    <>
      {/* {hasPortfolio ? null : <h2>Claim Your Portfolio URL</h2>} */}

      {hasPortfolio ? null : (
        <>
          <form onSubmit={handleCreate}>
            {/* <label>Desired URL Path:</label> */}
  
              {/* <span style={{ marginRight: "5px", fontWeight: "bold" }}>
                {rootDomain}/
              </span> */}
              
              <input
                type="text"
                placeholder="custom-name"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                style={{ padding: "8px", fontSize: "14px" }}
              />

              {/* REAL-TIME STATUS FEEDBACK INDICATION */}
              {checking && (
                <span
                  style={{
                    marginLeft: "10px",
                    fontSize: "12px",
                    color: "#6b7280",
                  }}
                >
                  (Checking...)
                </span>
              )}
              {!checking && isAvailable === true && (
                <span
                  style={{
                    marginLeft: "10px",
                    fontSize: "12px",
                    color: "#10b981",
                    fontWeight: "600",
                  }}
                >
                  (Available!)
                </span>
              )}
              {!checking && isAvailable === false && (
                <span
                  style={{
                    marginLeft: "10px",
                    fontSize: "12px",
                    color: "#ef4444",
                    fontWeight: "600",
                  }}
                >
                  (Taken!)
                </span>
              )}

            <button
              type="submit"
              disabled={loading || isAvailable === false}>
              {loading ? "Processing..." : <CreateNewFolderIcon />}
            </button>
          </form>
        </>
      )}

      <>
        <form onSubmit={handleUpdate}>
          <div className="generic-btn">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
              disabled={loading}
            />
            <AddPhotoAlternateIcon />
           </div>

          <input
            type="text"
            placeholder="John Doe"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={loading}
          />

          <textarea
            placeholder="Describe your work..."
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            disabled={loading}
          />

          <button
            type="submit"
            disabled={loading || isAvailable === false}
          >
            {loading ? "Processing..." : <UpdateIcon/>}
          </button>
        </form>
      </>

      {statusMessage && (
        <p style={{ marginTop: "20px", color: "#4f46e5", fontWeight: "bold" }}>
          {statusMessage}
        </p>
      )}
    </>
  );
}

export default CreatePortfolio;
